import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { paymentAPI } from '../utils/api';
import './ThankYou.css';

// Extend window interface for gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

interface TransactionDetails {
  transactionId: string;
  type: 'token_purchase' | 'subscription';
  amount: number;
  tokens?: number;
  tier?: string;
  email: string;
  createdAt: string;
}

const ThankYou: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transaction, setTransaction] = useState<TransactionDetails | null>(null);
  const [conversionTracked, setConversionTracked] = useState(false);

  useEffect(() => {
    const verifyTransaction = async () => {
      const sessionId = searchParams.get('session_id');
      const type = searchParams.get('type'); // 'payment' or 'subscription'
      
      if (!sessionId) {
        setError('Invalid access. No transaction found.');
        setLoading(false);
        return;
      }

      try {
        // Verify the session with the backend
        const response = await paymentAPI.verifySession(sessionId);
        
        if (!response.success || !response.transaction) {
          setError('Transaction not found or already processed.');
          setLoading(false);
          return;
        }

        setTransaction(response.transaction);
        
        // Track Google Ads conversion (only once)
        if (!conversionTracked && typeof window.gtag !== 'undefined') {
          const conversionValue = response.transaction.amount || 0;
          
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17820443433/YoShCPCR49QbEKnGubFC',
            'value': conversionValue,
            'currency': 'USD',
            'transaction_id': response.transaction.transactionId
          });
          
          // Also track a custom event for more detailed analytics
          window.gtag('event', 'purchase', {
            'transaction_id': response.transaction.transactionId,
            'value': conversionValue,
            'currency': 'USD',
            'items': [{
              'item_name': type === 'subscription' 
                ? `${response.transaction.tier} Subscription` 
                : `${response.transaction.tokens} Tokens`,
              'price': conversionValue,
              'quantity': 1
            }]
          });
          
          setConversionTracked(true);
          console.log('✅ Google Ads conversion tracked:', response.transaction.transactionId);
        }
        
        setLoading(false);
      } catch (err: any) {
        console.error('Failed to verify transaction:', err);
        setError('Unable to verify transaction. Please contact support if you were charged.');
        setLoading(false);
      }
    };

    verifyTransaction();
  }, [searchParams, conversionTracked]);

  if (loading) {
    return (
      <div className="thankyou-page">
        <div className="thankyou-container">
          <div className="thankyou-loading">
            <div className="loading-spinner"></div>
            <p>Verifying your transaction...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="thankyou-page">
        <div className="thankyou-container thankyou-error">
          <div className="error-icon">⚠️</div>
          <h1>Transaction Error</h1>
          <p>{error}</p>
          <div className="thankyou-actions">
            <Link to="/settings" className="btn btn-primary">
              Go to Settings
            </Link>
            <Link to="/dashboard" className="btn btn-secondary">
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isSubscription = transaction?.type === 'subscription';
  const formattedDate = transaction?.createdAt 
    ? new Date(transaction.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '';

  return (
    <div className="thankyou-page">
      <div className="thankyou-container">
        {/* Success Animation */}
        <div className="success-checkmark">
          <div className="check-icon">
            <span className="icon-line line-tip"></span>
            <span className="icon-line line-long"></span>
            <div className="icon-circle"></div>
            <div className="icon-fix"></div>
          </div>
        </div>

        <h1 className="thankyou-title">
          {isSubscription ? 'Thank You for Subscribing!' : 'Thank You for Your Purchase!'}
        </h1>
        
        <p className="thankyou-subtitle">
          {isSubscription 
            ? 'Your subscription has been activated successfully.' 
            : 'Your tokens have been credited to your account.'}
        </p>

        {/* Transaction Details Card */}
        <div className="transaction-card">
          <h2>Transaction Details</h2>
          
          <div className="transaction-row">
            <span className="transaction-label">Transaction ID:</span>
            <span className="transaction-value transaction-id">{transaction?.transactionId}</span>
          </div>
          
          <div className="transaction-row">
            <span className="transaction-label">Date:</span>
            <span className="transaction-value">{formattedDate}</span>
          </div>
          
          <div className="transaction-row">
            <span className="transaction-label">Type:</span>
            <span className="transaction-value">
              {isSubscription ? `${transaction?.tier} Subscription` : 'Token Purchase'}
            </span>
          </div>
          
          {!isSubscription && transaction?.tokens && (
            <div className="transaction-row">
              <span className="transaction-label">Tokens Added:</span>
              <span className="transaction-value tokens-value">+{transaction.tokens}</span>
            </div>
          )}
          
          <div className="transaction-row">
            <span className="transaction-label">Amount Paid:</span>
            <span className="transaction-value amount-value">${transaction?.amount?.toFixed(2)}</span>
          </div>
          
          <div className="transaction-row">
            <span className="transaction-label">Email:</span>
            <span className="transaction-value">{transaction?.email}</span>
          </div>
        </div>

        {/* What's Next Section */}
        <div className="whats-next">
          <h3>What's Next?</h3>
          {isSubscription ? (
            <ul>
              <li>✨ Your monthly credits have been added to your account</li>
              <li>📚 Start creating amazing storybooks with AI</li>
              <li>💾 Enjoy increased storage space</li>
              <li>🎨 Access premium features with your new plan</li>
            </ul>
          ) : (
            <ul>
              <li>✨ Your tokens are ready to use immediately</li>
              <li>🎨 Generate characters, backgrounds, and scenes</li>
              <li>📚 Create beautiful AI-powered storybooks</li>
              <li>💡 Tokens never expire - use them anytime</li>
            </ul>
          )}
        </div>

        {/* Action Buttons */}
        <div className="thankyou-actions">
          <Link to="/dashboard" className="btn btn-primary btn-large">
            Start Creating
          </Link>
          <Link to="/settings?tab=usage" className="btn btn-secondary">
            View Your Balance
          </Link>
        </div>

        {/* Support Note */}
        <p className="support-note">
          Need help? Contact us at <a href="mailto:support@tinylittlefoxstudio.com">support@tinylittlefoxstudio.com</a>
        </p>
      </div>
    </div>
  );
};

export default ThankYou;
